'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils/cx';

export type PopoverAlign = 'left' | 'right';

export type PopoverCloseOptions = {
  returnFocus?: boolean;
};

export interface PopoverProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  closeOnSelect?: boolean;
}

export interface PopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  unstyled?: boolean;
}

export interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  align?: PopoverAlign;
  closeOnSelect?: boolean;
}

interface PopoverContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  close: (options?: PopoverCloseOptions) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  panelId: string;
  closeOnSelectDefault: boolean;
}

const PopoverContext = createContext<PopoverContextType | null>(null);

function isDataAttrTruthy(el: HTMLElement, attrName: string): boolean {
  const v = el.getAttribute(attrName);
  if (v == null) return false;
  return v !== 'false';
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function Popover({ children, className, closeOnSelect = true, ...rest }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const lastOwnerKeyRef = useRef<string | null>(null);

  const reactId = useId();
  const panelId = useMemo(() => `popover-panel-${reactId}`, [reactId]);

  const close = useCallback((options?: PopoverCloseOptions) => {
    setOpen(false);

    const shouldReturnFocus = options?.returnFocus !== false;
    if (!shouldReturnFocus) return;

    requestAnimationFrame(() => {
      const trg = triggerRef.current;
      if (!trg) return;
      if (!document.contains(trg)) return;
      trg.focus?.();
    });
  }, []);

  useLayoutEffect(() => {
    const ownerKey = triggerRef.current?.getAttribute('data-popover-owner-key') ?? null;

    if (lastOwnerKeyRef.current === null) {
      lastOwnerKeyRef.current = ownerKey;
      return;
    }

    if (open && ownerKey && lastOwnerKeyRef.current !== ownerKey) {
      lastOwnerKeyRef.current = ownerKey;
      setOpen(false);
      return;
    }

    lastOwnerKeyRef.current = ownerKey;
  });

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const trg = triggerRef.current;
      const panel = contentRef.current;
      const target = e.target as Node | null;

      if (!target) return;
      if (trg?.contains(target) || panel?.contains(target)) return;

      close({ returnFocus: true });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close({ returnFocus: true });
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return (
    <PopoverContext.Provider
      value={{
        open,
        setOpen,
        close,
        triggerRef,
        contentRef,
        panelId,
        closeOnSelectDefault: closeOnSelect,
      }}
    >
<div className={cx('relative inline-block', className)} {...rest}>
        {children}
      </div>
</PopoverContext.Provider>
  );
}

export function PopoverTrigger({ children, className, unstyled, ...rest }: PopoverTriggerProps) {
  const context = useContext(PopoverContext);
  if (!context) throw new Error('PopoverTrigger must be used within a Popover');

  const { open, setOpen, triggerRef, panelId } = context;

  const toggle = () => setOpen(!open);

  const buttonClass = unstyled
    ? className
    : cx('inline-flex items-center justify-center rounded-lg focus:outline-none focus-visible:ring-2', className);

  return (
    <button
      ref={triggerRef}
      type="button"
      aria-expanded={open}
      aria-haspopup="menu"
      aria-controls={open ? panelId : undefined}
      onClick={toggle}
      className={buttonClass}
      {...rest}
    >
      {children}
    </button>
  );
}

export function PopoverContent({ children, className, style, align = 'right', closeOnSelect, ...rest }: PopoverContentProps) {
  const context = useContext(PopoverContext);
  if (!context) throw new Error('PopoverContent must be used within a Popover');

  const { open, triggerRef, contentRef, close, panelId, closeOnSelectDefault } = context;

  const effectiveCloseOnSelect = closeOnSelect ?? closeOnSelectDefault;

  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;

    const update = () => {
      const trg = triggerRef.current;
      const panel = contentRef.current;
      if (!trg || !panel) return;

      const rect = trg.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const gap = 8;

      const top = rect.bottom + gap;
      const left =
        align === 'left'
          ? rect.right - panelRect.width
          : rect.left;

      const clampedLeft = clamp(left, 8, window.innerWidth - panelRect.width - 8);
      const clampedTop = clamp(top, 8, window.innerHeight - panelRect.height - 8);

      setPos({ top: clampedTop, left: clampedLeft });
    };

    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [align, contentRef, open, triggerRef]);

  if (!open) return null;

  const onClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const explicitCloseEl = target.closest<HTMLElement>('[data-popover-close]');
    if (explicitCloseEl && isDataAttrTruthy(explicitCloseEl, 'data-popover-close')) {
      close({ returnFocus: false });
      return;
    }

    if (!effectiveCloseOnSelect) return;

    const keepOpenEl = target.closest<HTMLElement>('[data-popover-keep-open]');
    if (keepOpenEl && isDataAttrTruthy(keepOpenEl, 'data-popover-keep-open')) return;

    const interactive = target.closest("button, a, [role='menuitem'], input, select, textarea, [data-popover-close='true']");
    if (interactive) close({ returnFocus: false });
  };

  const content = (
    <div
      ref={contentRef}
      id={panelId}
      role="menu"
      tabIndex={-1}
      onClick={onClick}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        ...style,
      }}
      className={cx('z-50', className)}
      {...rest}
    >
      {children}
    </div>
  );

  return createPortal(content, document.body);
}

Popover.displayName = 'Popover';
PopoverTrigger.displayName = 'PopoverTrigger';
PopoverContent.displayName = 'PopoverContent';